import { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useValidation } from '@hooks/validations';
import { Dropdown, MultiselectDropdown, SubmitButton, TextInput, RadioButtonChip } from '@components/common';
import { glueDetailsInitialValues, GlueUsedLabel, glueUsedList, gradeUsedList, substratesOptions } from '.';
import { keyDownEvent, onImageError } from '@helper/utils';
import { brandUsedList, brandUsedListLMS } from '@config/constant';
import { useCompetitionConversionUsageQuery } from '@services/hooks/competition-conversion/useCompetitionConversionUsageQuery';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { ProductImages } from '@components/siteLeads/components/siteProducts/productImages';
import { useImageUpload } from '@hooks/useImageUpload';
import { usePresignedURLQuery } from '@services/hooks/enduser-details/usePresignedURLQuery';
import { IAddNewSiteActivePayload, useAddEditNewSiteMutation } from '@services/hooks/lms/addNewSite';
import { ToastType, useTosterStore } from '@store/toster';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { isNullOrEmpty, parseMultiselectOptions } from '@components/delaerVisit/components/addEditDealerVisit';
import { ContractorTypeLabel } from '@components/lms/addNewSite/components/meetingDetails';
import { useLocation, useNavigate } from 'react-router-dom';
import { useComponentLevelAccessCheck } from '../../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useEndUserStore } from '@store/enduser';
import { useSiteContext } from '../../addNewSite';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { useUploadSiteImagesMutation } from '@services/hooks/site-leads/useUploadSiteImagesMutation';
import { useProductRecommendationNavigation } from '@services/hooks/lms/addNewSite/useProductRecommendationNavigation';
import { useSiteVisitActiveUrlQuery } from '@services/hooks/site-leads/useSiteVisitActiveUrlQuery';

export const GlueDetails = (): ReactElement => {
	const { siteData, isEditing } = useSiteContext();
	useSetMainHeader(isEditing ? 'Site Details' : 'Add New Site');
	const navigate = useNavigate();
	const { activeTabIndex, visitData, formId, isConvertingUpcomingToActive, setActiveTabIndex } = useEndUserStore();
	const { clearSiteDetails } = useSiteAllDetailsStore();
	const { state } = useLocation();
	const { visit } = state ?? {};
	// Clear site details store when component unmounts for new sites
	useEffect(() => {
		return (): void => {
			if (!isEditing) {
				clearSiteDetails();
			}
		};
	}, [isEditing, clearSiteDetails]);

	// Custom tab navigation guard that only applies when not in edit mode
	useEffect(() => {
		const currentPath = window.location.pathname;
		const isNavigatingToGlueDetails = currentPath.includes('/glue-details');

		// Don't redirect if we're specifically navigating to glue details
		if (!isEditing && activeTabIndex < 2 && !isNavigatingToGlueDetails) {
			navigate('/lms/add-new-site/site-information', { replace: true });
		}
	}, [activeTabIndex, navigate, isEditing]);

	const { setToaster } = useTosterStore();
	const { setSubKeyValue } = useAddNewSiteStore();
	const { LmsGlueDetailsValidationSchema } = useValidation();
	const { handleAddImage } = useImageUpload();

	// Set active tab index to 2 (glue details) when in edit mode
	useEffect(() => {
		if (isEditing) {
			setActiveTabIndex(2);
		}
	}, [isEditing, setActiveTabIndex]);

	const {
		isPending: isCompetitionListPending,
		data: competitionList,
		error: competitionListError,
	} = useCompetitionConversionUsageQuery();
	const { mutate: mutatePresignedURL, data: getSiteImagesPresignedUrl } = usePresignedURLQuery();
	const { mutate: putSiteImagesData } = useUploadSiteImagesMutation(getSiteImagesPresignedUrl);
	const { mutate: addEditNewSiteMutation } = useAddEditNewSiteMutation(
		isConvertingUpcomingToActive && formId ? formId : undefined
	);
	const { handleProductRecommendationAndNavigate } = useProductRecommendationNavigation();

	const permissionSSDropdownCheck = useComponentLevelAccessCheck('bdese-ss-dropdown');
	const [maxLimit, setMaxLimit] = useState(3);
	const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);
	const { data: activeUrlData } = useSiteVisitActiveUrlQuery();
	const activeLink = activeUrlData?.data?.[0];
	const [hasOpenedActiveLink, setHasOpenedActiveLink] = useState(false);

	const formik = useFormik({
		initialValues: glueDetailsInitialValues,
		validationSchema: LmsGlueDetailsValidationSchema,
		onSubmit: () => {
			handleSubmitForm();
		},
	});

	const isCompetition = formik.values.glueUsedAtSite === GlueUsedLabel.Competition;

	const handleMultiSelectChange = (e: DropdownOptions): void => {
		const hasNA = formik.values.substratesCombinations.some((v) => v.value === 'NA');
		const isNASelected = e.value === 'NA';
		setMaxLimit(isNASelected && !hasNA ? 1 : 3);

		if (isNASelected) {
			formik.setFieldValue('substratesCombinations', hasNA ? [] : [{ id: 7, value: 'NA' }]);
		}
	};

	const competitionOptions = useMemo(() => {
		if (!competitionList?.length) return [];

		return competitionList.map((competition, index) => {
			return {
				id: index,
				value: competition.name,
			};
		});
	}, [competitionList]);

	const handleImageAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const maxImageLimit = 5;
			const folderName = 'sitelead';
			const maximumFileSize = 20 * 1024 * 1024;
			const result = handleAddImage(event, formik.values.productImages, maximumFileSize, maxImageLimit, folderName);
			if (result) {
				const { payload, updatedImages } = result;
				updatedImages && formik.setFieldValue('productImages', updatedImages);
				if (payload) {
					mutatePresignedURL(payload, {
						onSuccess: (presignedUrls) => {
							setUploadedImageUrls(presignedUrls.map((img) => img.normal_url));
						},
					});
				}
			}
		},
		[formik, handleAddImage, mutatePresignedURL]
	);

	const handleAddNewSiteApi = useCallback(() => {
		const addNewSiteStore = useAddNewSiteStore.getState();
		const [dealerCode, dealerName] = addNewSiteStore.meetingDetails.dealerName.split('-').map((part) => part.trim());

		const visitImages = uploadedImageUrls;
		const visitImageFields: { [key: string]: string | null } = {};
		for (let i = 0; i < 5; i++) {
			visitImageFields[`visit_image${i + 1}`] = visitImages[i] || null;
		}

		const payload: IAddNewSiteActivePayload = {
			site_lead: {
				/* site info fields */
				name: addNewSiteStore.siteInfo.siteName,
				site_location_c: addNewSiteStore.siteInfo.siteLocation,
				address_c: addNewSiteStore.siteInfo.siteAddress,
				latitude: addNewSiteStore.siteInfo.latitude,
				longitude: addNewSiteStore.siteInfo.longitude,
				pincode_c: addNewSiteStore.siteInfo.pincode,
				town_name_c: addNewSiteStore.siteInfo.city,
				site_sub_type_c: addNewSiteStore.siteInfo.siteType,
				type_of_property: addNewSiteStore.siteInfo.typeOfProperty,
				application_area_c: addNewSiteStore.siteInfo.propertyArea,
				site_current_status_c: addNewSiteStore.siteInfo.siteStage,

				/* meeting details fields */
				whom_did_you_meet_c: addNewSiteStore.meetingDetails.contractorType,
				supervisor_name: addNewSiteStore.meetingDetails.contractorName,
				supervisor_phone_number: addNewSiteStore.meetingDetails.contractorPhoneNumber,
				...(addNewSiteStore.meetingDetails.contractorType === ContractorTypeLabel.Contractor
					? {
							type_of_contracts: addNewSiteStore.typeOfContracts.replace(/\s+/g, '_'),
							no_of_sites: addNewSiteStore.noOfSites,
							contractor_potential_per_month: addNewSiteStore.contractorPotentialPerMonth,
							lead_app_id_c: addNewSiteStore.appIdC,
							end_user_c: addNewSiteStore.endUserC,
						}
					: {}),
				home_owner_name_c: addNewSiteStore.meetingDetails.homeOwnerDetails[0]?.name,
				home_owner_mobile_number_c: addNewSiteStore.meetingDetails.homeOwnerDetails[0].number,
				architect_name_c: addNewSiteStore.meetingDetails.architectDetails[0].name,
				architect_mobile_no_c: addNewSiteStore.meetingDetails.architectDetails[0].number,
				builder_name: addNewSiteStore.meetingDetails.builderDetails[0]?.name,
				builder_phone_number: addNewSiteStore.meetingDetails.builderDetails[0]?.number,
				builder_office_address: addNewSiteStore.meetingDetails.builderDetails[0]?.address,
				dealer_name_c: dealerName,
				dealer_code_c: dealerCode,
				...(permissionSSDropdownCheck && {
					assigned_cmdi_c: addNewSiteStore.meetingDetails.assigneCmdi?.split('-')[0]?.trim(),
				}),

				/* glue details fields */
				glue_used_at_the_site: addNewSiteStore.glueDetails.glueUsedAtSite,
				grade_glue_used_at_the_site: addNewSiteStore.glueDetails.gradeUsed,
				brand_available_at_site:
					addNewSiteStore.glueDetails.brandAvailable === 'Others'
						? addNewSiteStore.glueDetails.otherBrandName ?? ''
						: addNewSiteStore.glueDetails.brandAvailable ?? '',
				glue_potential_in_kg: addNewSiteStore.glueDetails.gluePotentialOfSite,
				reason_of_competition: addNewSiteStore.glueDetails.competitionReason,
				glueconsumedtilldate_c: addNewSiteStore.glueDetails.glueUsedSoFar,
				conversion_potential_in_kg: addNewSiteStore.glueDetails.conversionPotential,
				substrate_available: isNullOrEmpty(addNewSiteStore.glueDetails.substratesCombinations) ?? '',
			},
			visit: {
				...visitImageFields,
			},
		};
		if (formik.values.productImages.length > 0) {
			putSiteImagesData(formik.values.productImages, {
				onSuccess: () => {
					addEditNewSiteMutation(payload, {
						onSuccess: () => {
							setToaster(
								true,
								ToastType.success,
								isConvertingUpcomingToActive ? 'Site converted to Active successfully' : 'Site lead added successfully!'
							);
							// Call product recommendation API and navigate based on response
							handleProductRecommendationAndNavigate({
								grade: addNewSiteStore.glueDetails.gradeUsed,
								reason: addNewSiteStore.glueDetails.competitionReason,
								type_of_contract: addNewSiteStore.typeOfContracts,
								glue: addNewSiteStore.glueDetails.glueUsedAtSite,
								membershipno: addNewSiteStore.membershipNo,
								isConvertingUpcomingToActive,
							});
						},
						onError: (error: Error) => {
							setToaster(true, ToastType.error, error.message);
						},
					});
				},
				onError: (error: Error) => {
					setToaster(true, ToastType.error, 'Failed to upload images: ' + error.message);
				},
			});
		} else {
			// No images case (shouldn't happen due to validation, but handled for safety)
			addEditNewSiteMutation(payload, {
				onSuccess: () => {
					setToaster(
						true,
						ToastType.success,
						isConvertingUpcomingToActive ? 'Site converted to Active successfully' : 'Site lead added successfully!'
					);
					// Call product recommendation API and navigate based on response
					handleProductRecommendationAndNavigate({
						grade: addNewSiteStore.glueDetails.gradeUsed,
						reason: addNewSiteStore.glueDetails.competitionReason,
						type_of_contract: addNewSiteStore.typeOfContracts,
						glue: addNewSiteStore.glueDetails.glueUsedAtSite,
						membershipno: addNewSiteStore.membershipNo,
						isConvertingUpcomingToActive,
					});
				},
				onError: (error: Error) => {
					setToaster(true, ToastType.error, error.message);
				},
			});
		}
	}, [
		uploadedImageUrls,
		permissionSSDropdownCheck,
		addEditNewSiteMutation,
		setToaster,
		isConvertingUpcomingToActive,
		handleProductRecommendationAndNavigate,
		formik.values.productImages,
		putSiteImagesData,
	]);

	const handleSubmitForm = useCallback(() => {
		const keys = [
			'glueUsedAtSite',
			'gradeUsed',
			'brandAvailable',
			'gluePotentialOfSite',
			'competitionReason',
			'glueUsedSoFar',
			'conversionPotential',
			'substratesCombinations',
			'otherBrandName',
		] as const;

		keys.forEach((key) => {
			setSubKeyValue('glueDetails', key, formik.values[key]);
		});

		handleAddNewSiteApi();
	}, [formik.values, handleAddNewSiteApi, setSubKeyValue]);

	useEffect(() => {
		const store = useAddNewSiteStore.getState();

		if (!formik.values.glueUsedAtSite && !formik.values.gradeUsed) {
			const initialValues = {
				glueUsedAtSite: store.glueDetails.glueUsedAtSite,
				gradeUsed: store.glueDetails.gradeUsed,
				brandAvailable: store.glueDetails.brandAvailable,
				gluePotentialOfSite: store.glueDetails.gluePotentialOfSite,
				competitionReason: store.glueDetails.competitionReason,
				glueUsedSoFar: store.glueDetails.glueUsedSoFar,
				conversionPotential: store.glueDetails.conversionPotential,
				substratesCombinations: [],
				productImages: [],
			};
			formik.setValues(initialValues);
		}

		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if ((isEditing || isConvertingUpcomingToActive) && siteData) {
			const store = useAddNewSiteStore.getState();
			if (!store.glueDetails.glueUsedAtSite && !store.glueDetails.gradeUsed) {
				formik.setValues({
					glueUsedAtSite: siteData.glue_used_at_the_site ?? '',
					gradeUsed: siteData.grade_glue_used_at_the_site ?? '',
					brandAvailable: siteData.brand_available_at_site ?? '',
					gluePotentialOfSite: siteData.glue_potential_in_kg ?? '',
					competitionReason: siteData.reason_of_competition ?? '',
					glueUsedSoFar: siteData.glueconsumedtilldate_c ?? '',
					conversionPotential: siteData.conversion_potential_in_kg ?? '',
					substratesCombinations: parseMultiselectOptions(siteData.substrate_available, substratesOptions),
					productImages: [],
				});
				if (
					(visitData && visitData?.event_image_c) ||
					[
						visitData?.event_image_c,
						visitData?.event_image2_c,
						visitData?.event_image3_c,
						visitData?.event_image4_c,
						visitData?.event_image5_c,
					].some(Boolean)
				) {
					const siteDataImages = [
						visitData?.event_image_c,
						visitData?.event_image2_c,
						visitData?.event_image3_c,
						visitData?.event_image4_c,
						visitData?.event_image5_c,
					].filter((img): img is string => Boolean(img));

					if (siteDataImages.length > 0) {
						setUploadedImageUrls(siteDataImages);
					}
				}

				if (
					(visit && visit?.event_image_c) ||
					visit?.event_image2_c ||
					visit?.event_image3_c ||
					visit?.event_image4_c ||
					visit?.event_image5_c
				) {
					const siteDataImages = [
						visit.event_image_c,
						visit.event_image2_c,
						visit.event_image3_c,
						visit.event_image4_c,
						visit.event_image5_c,
					].filter(Boolean);

					setUploadedImageUrls(siteDataImages);
				}
			}
		}

		// eslint-disable-next-line
	}, [siteData, isEditing]);

	useEffect(() => {
		const gluePotential = Number(formik.values.gluePotentialOfSite) || 0;
		const glueUsed = Number(formik.values.glueUsedSoFar) || 0;
		const conversionPotential = gluePotential - glueUsed;
		formik.setFieldValue('conversionPotential', conversionPotential > 0 ? conversionPotential : 0, false);

		// eslint-disable-next-line
	}, [formik.values.gluePotentialOfSite, formik.values.glueUsedSoFar]);

	return (
		<div className="add-new-site-form">
			<Wrapper isLoaderVisible={isCompetitionListPending} isError={competitionListError}>
				<form onSubmit={formik.handleSubmit}>
					<ul className="form-wrapper row">
						<li className="d-col d-col-2">
							<RadioButtonChip
								label="Glue Used at Site"
								options={glueUsedList}
								value={formik.values.glueUsedAtSite ?? ''}
								onChange={(val) => {
									formik.setFieldValue('glueUsedAtSite', val);
									formik.setFieldValue('brandAvailable', '');
									formik.setFieldValue('gradeUsed', '');
								}}
								onBlur={() => formik.setFieldTouched('glueUsedAtSite', true)}
								error={
									formik.touched.glueUsedAtSite && formik.errors.glueUsedAtSite ? formik.errors.glueUsedAtSite : null
								}
								required
								disabled={isEditing}
							/>
						</li>
						<li className="form-control d-col d-col-2">
							<TextInput
								type="text"
								label="Glue Potential of the site in KG"
								id="gluePotentialOfSite"
								name="gluePotentialOfSite"
								onBlur={formik.handleBlur}
								value={formik.values.gluePotentialOfSite ?? ''}
								setFieldValue={formik.setFieldValue}
								onKeyDown={keyDownEvent}
								error={
									formik.touched.gluePotentialOfSite && formik.errors.gluePotentialOfSite
										? formik.errors.gluePotentialOfSite
										: null
								}
								required
								disabled={isEditing}
							/>
						</li>
						{isCompetition ? (
							<li className="d-col d-col-2">
								<Dropdown
									label="Brand Available at Site"
									id="brandAvailable"
									name="brandAvailable"
									title={formik.values.brandAvailable ?? ''}
									options={brandUsedList}
									setFieldValue={formik.setFieldValue}
									onBlur={formik.handleBlur}
									onChange={(e) => {
										formik.setFieldValue('brandUsed', e.value);
										const selectedBrand = brandUsedList.find((item) => item.value === e.value);
										formik.setFieldValue('gradeUsed', selectedBrand?.grade ?? '');
									}}
									error={
										formik.touched.brandAvailable && formik.errors.brandAvailable ? formik.errors.brandAvailable : null
									}
									required
									disabled={isEditing}
								/>
							</li>
						) : (
							<li className="d-col d-col-2">
								<Dropdown
									label="Brand Available at Site"
									id="brandAvailable"
									name="brandAvailable"
									title={formik.values.brandAvailable ?? ''}
									options={brandUsedListLMS.filter((brand) => brand.value !== 'Others')}
									setFieldValue={formik.setFieldValue}
									onBlur={formik.handleBlur}
									error={
										formik.touched.brandAvailable && formik.errors.brandAvailable ? formik.errors.brandAvailable : null
									}
									required
									disabled={isEditing}
								/>
							</li>
						)}

						<li className="d-col d-col-2">
							<RadioButtonChip
								label="Grade of Glue used at Site"
								options={gradeUsedList}
								value={formik.values.gradeUsed ?? ''}
								onChange={(val) => formik.setFieldValue('gradeUsed', val)}
								onBlur={() => formik.setFieldTouched('gradeUsed', true)}
								error={formik.touched.gradeUsed && formik.errors.gradeUsed ? formik.errors.gradeUsed : null}
								required
								disabled={
									isEditing || !!siteData?.grade_glue_used_at_the_site || formik.values.glueUsedAtSite === 'Competition'
								}
							/>
						</li>
						{isCompetition && formik.values.brandAvailable === 'Others' && (
							<li className="form-control d-col d-col-2">
								<TextInput
									type="text"
									label="Other Brand Name"
									id="otherBrandName"
									name="otherBrandName"
									value={formik.values.otherBrandName ?? ''}
									setFieldValue={formik.setFieldValue}
									onBlur={formik.handleBlur}
									error={
										formik.touched.otherBrandName && formik.errors.otherBrandName ? formik.errors.otherBrandName : null
									}
									required
									disabled={isEditing}
								/>
							</li>
						)}

						{isCompetition && (
							<>
								<li className="d-col d-col-2">
									<Dropdown
										label="Reason for Competition"
										id="competitionReason"
										name="competitionReason"
										title={formik.values.competitionReason ?? ''}
										options={competitionOptions}
										setFieldValue={formik.setFieldValue}
										onBlur={formik.handleBlur}
										error={
											formik.touched.competitionReason && formik.errors.competitionReason
												? formik.errors.competitionReason
												: null
										}
										required
										disabled={isEditing}
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										type="text"
										label="Glue used so far"
										id="glueUsedSoFar"
										name="glueUsedSoFar"
										onBlur={formik.handleBlur}
										value={formik.values.glueUsedSoFar ?? ''}
										setFieldValue={formik.setFieldValue}
										onKeyDown={keyDownEvent}
										error={
											formik.touched.glueUsedSoFar && formik.errors.glueUsedSoFar ? formik.errors.glueUsedSoFar : null
										}
										required
										disabled={isEditing}
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										type="text"
										label="Conversion Potential in KG"
										id="conversionPotential"
										name="conversionPotential"
										onBlur={formik.handleBlur}
										value={formik.values.conversionPotential ?? ''}
										setFieldValue={formik.setFieldValue}
										error={
											formik.touched.conversionPotential && formik.errors.conversionPotential
												? formik.errors.conversionPotential
												: null
										}
										disabled
										required
									/>
								</li>
							</>
						)}

						<li className="d-col d-col-2 single-line-multiselect">
							<MultiselectDropdown
								label="Substrate Available"
								id="substratesCombinations"
								name="substratesCombinations"
								className="input-border single-lable-multi-dropdown"
								options={substratesOptions}
								values={formik.values.substratesCombinations}
								setFieldValue={formik.setFieldValue}
								onChange={handleMultiSelectChange}
								maxLimit={maxLimit}
								onBlur={formik.handleBlur}
								error={
									formik.touched.substratesCombinations && formik.errors.substratesCombinations
										? formik.errors.substratesCombinations.toString()
										: null
								}
								required
								disabled={isEditing}
							/>
						</li>
						<li className="d-col d-col-2">
							<ProductImages
								fieldName="productImages"
								formik={formik}
								handleAddImage={handleImageAdd}
								required
								showAddButton={!isEditing}
								showRemoveButton={!isEditing}
							/>
							{isEditing && uploadedImageUrls.length > 0 && (
								<div className="upload-images-wrapper">
									{uploadedImageUrls?.map((image: string) => (
										<div key={`${image}`} className="upload-image">
											<div className="wrapper-upload-images">
												<img
													role="presentation"
													src={image}
													alt={image}
													title="site"
													width="56"
													height="56"
													onError={onImageError}
												/>
											</div>
										</div>
									))}
								</div>
							)}
						</li>
						{activeLink && !isEditing && (
							<>
								<li className="d-col d-col-2">
									<div className="feedback-common-wrapper">
										<span className="h4 refrence-heading">
											Reference Forms
											{activeLink?.mandatory && <span className="bps-required-star">*</span>}
										</span>
										<div className="feedback-content">
											<p>
												Click link to fill form{' '}
												<a
													aria-label="feedback-form"
													href={activeLink.url}
													target="_blank"
													rel="noreferrer"
													onClick={(): void => {
														setHasOpenedActiveLink(true);
													}}
												>
													- {activeLink.display_value}
												</a>
											</p>
										</div>
									</div>
								</li>
							</>
						)}
					</ul>
					<SubmitButton
						label="Submit"
						isDisabled={
							!formik.isValid ||
							!formik.dirty ||
							!formik.values.productImages.length ||
							(activeLink?.mandatory && !hasOpenedActiveLink)
						}
						handleSubmitClick={formik.handleSubmit}
					/>
				</form>
			</Wrapper>
		</div>
	);
};
